import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Play } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const Hero = () => {
  const { language } = useLanguage();
  
  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center overflow-hidden">
      {/* Video Background Placeholder - Dark overlay */}
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920')] bg-cover bg-center">
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      {/* Animated gradient orbs */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-[#84bc00] rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute top-1/3 -right-20 w-96 h-96 bg-[#ffb84d] rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-[#0ea5a5] rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 pt-40 text-center">
        {/* Verified Badge */}
        <div className="inline-flex items-center space-x-2 bg-[#84bc00]/10 border border-[#84bc00]/50 backdrop-blur-sm rounded-full px-4 py-2 mb-8 animate-fade-in">
          <Leaf className="text-[#84bc00]" size={16} />
          <span className="text-[#84bc00] text-sm font-bold tracking-wide">
            {language === 'es' ? '100% ECO-CERTIFICADO' : '100% ECO-CERTIFIED'}
          </span>
        </div>

        {/* Main Heading */}
        <h1 className="text-4xl sm:text-5xl lg:text-7xl xl:text-8xl font-black text-white mb-6 leading-tight animate-fade-in-up" data-testid="hero-title">
          <span className="block">{language === 'es' ? 'TU LISTA DE DESEOS' : 'YOUR BUCKET LIST'}</span>
          <span className="block text-[#84bc00]">{language === 'es' ? 'NO DEBERÍA DAÑAR' : "SHOULDN'T BREAK"}</span>
          <span className="block text-[#ffb84d]">{language === 'es' ? 'EL PLANETA' : 'THE PLANET'}</span>
        </h1>

        {/* Subtitle */}
        <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto mb-12 animate-fade-in-up animation-delay-200">
          {language === 'es' 
            ? 'Viajes de aventura conscientes en América Latina y el Caribe. Desde relajación hasta adrenalina extrema.'
            : 'Conscious adventure travel in Latin America & the Caribbean. From chill vibes to full-on badass.'}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-in-up animation-delay-400">
          <Link to="/trips">
            <Button 
              data-testid="find-your-trip-btn"
              className="bg-[#84bc00] hover:bg-[#6a9600] text-black text-lg font-bold px-8 py-6 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#84bc00]/30 flex items-center space-x-2"
            >
              <span>{language === 'es' ? 'ENCUENTRA TU VIAJE' : 'FIND YOUR TRIP'}</span>
              <ArrowRight size={20} />
            </Button>
          </Link>
          <Link to="/plan-my-trip">
            <Button 
              data-testid="plan-my-trip-btn"
              variant="outline" 
              className="border-2 border-white/50 text-white hover:bg-white hover:text-black text-lg font-bold px-8 py-6 rounded-lg transition-all duration-300 hover:scale-105 backdrop-blur-sm"
            >
              {language === 'es' ? 'PLANIFICA MI VIAJE' : 'PLAN MY TRIP'}
            </Button>
          </Link>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto animate-fade-in-up animation-delay-600">
          <div className="text-center p-4 rounded-xl bg-white/5 backdrop-blur-sm">
            <div className="text-3xl sm:text-4xl font-black text-[#84bc00] mb-1">8+</div>
            <div className="text-xs sm:text-sm text-gray-400 font-medium">
              {language === 'es' ? 'DESTINOS' : 'DESTINATIONS'}
            </div>
          </div>
          <div className="text-center p-4 rounded-xl bg-white/5 backdrop-blur-sm">
            <div className="text-3xl sm:text-4xl font-black text-[#ffb84d] mb-1">3</div>
            <div className="text-xs sm:text-sm text-gray-400 font-medium">
              {language === 'es' ? 'NIVELES DE INTENSIDAD' : 'INTENSITY LEVELS'}
            </div>
          </div>
          <div className="text-center p-4 rounded-xl bg-white/5 backdrop-blur-sm">
            <div className="text-3xl sm:text-4xl font-black text-[#0ea5a5] mb-1">100%</div>
            <div className="text-xs sm:text-sm text-gray-400 font-medium">
              {language === 'es' ? 'SOSTENIBLE' : 'SUSTAINABLE'}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-[#84bc00]/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-[#84bc00] rounded-full animate-scroll"></div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
